import React, { useState, useEffect, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { FiChevronLeft } from "react-icons/fi";
import LogoHeader from "./LogoHeader";
import {
  crearCartaPorte,
  fetchMaquinas,
  fetchClientesPorte,
  fetchConductoresCartaPorte,
  fetchCamionesCartaPorte,
  fetchPlantasArido,
} from "../services/cartas_porte";
import { fetchObras, fetchVertederos } from "../services/albaranes";
import {
  setObras,
  setMaquinas,
  setPlantasArido,
  setVertederos,
  setCartaPortePendiente,
} from "../redux/actions";
import "./CartaPorteForm.css";
import { useConnectionStatus } from "../hooks/useConnectionStatus";
import { ordenarPorTexto } from "../utils/ordenar";

/** Destino select values: obra id | "vertedero_{id}" | "v{id}" | "otro" */
function parseDestinoValue(value) {
  if (!value) {
    return { tipo: null, obraId: null, vertederoId: null, esOtro: false };
  }
  if (value === "otro") {
    return { tipo: "otro", obraId: null, vertederoId: null, esOtro: true };
  }
  if (typeof value === "string") {
    const m = value.match(/^(?:vertedero_|v)(\d+)$/);
    if (m) {
      return {
        tipo: "vertedero",
        obraId: null,
        vertederoId: m[1],
        esOtro: false,
      };
    }
  }
  return {
    tipo: "obra",
    obraId: value,
    vertederoId: null,
    esOtro: false,
  };
}

// Selector reutilizable de empresa (cargador / destinatario) para carta de máquina.
// Replica el criterio de autocompletado del backoffice: empresa propia, cliente de porte u "otro" manual.
function EmpresaSelector({
  control,
  setValue,
  watch,
  prefix,
  label,
  clientes,
  operadorInfo,
}) {
  const selectorName = `${prefix}_selector`;
  const seleccion = watch(selectorName);
  const esManual = seleccion === "otro";

  useEffect(() => {
    if (seleccion === undefined || seleccion === "") return;

    if (seleccion === "otro") {
      setValue(`${prefix}_cliente_id`, null);
      return;
    }

    if (seleccion === "propio") {
      setValue(`${prefix}_cliente_id`, null);
      setValue(`${prefix}_razon_social`, operadorInfo?.nombre || "");
      setValue(`${prefix}_cif`, operadorInfo?.cif || "");
      setValue(`${prefix}_direccion`, operadorInfo?.direccion || "");
      setValue(`${prefix}_tel`, operadorInfo?.telf || "");
      return;
    }

    const cliente = clientes.find((c) => String(c.id) === String(seleccion));
    setValue(`${prefix}_cliente_id`, cliente?.id ?? null);
    setValue(`${prefix}_razon_social`, cliente?.razon_social || "");
    setValue(`${prefix}_cif`, cliente?.cif || "");
    setValue(`${prefix}_direccion`, cliente?.direccion || "");
    setValue(`${prefix}_tel`, cliente?.telefono || "");
  }, [seleccion, prefix, clientes, operadorInfo, setValue]);

  return (
    <div className="form-section">
      <h2>{label}</h2>
      <div className="form-group">
        <label>Selecciona {label.toLowerCase()}</label>
        <Controller
          name={selectorName}
          control={control}
          render={({ field }) => (
            <select {...field}>
              <option value="">Selecciona...</option>
              {operadorInfo && (
                <option value="propio">
                  ★ {operadorInfo.nombre} (empresa propia)
                </option>
              )}
              {ordenarPorTexto(clientes, (c) => c.razon_social).map((cliente) => (
                <option key={cliente.id} value={cliente.id}>
                  {cliente.razon_social}
                </option>
              ))}
              <option value="otro">— Otro (rellenar manualmente) —</option>
            </select>
          )}
        />
      </div>

      <div className="form-group">
        <label>Razón Social</label>
        <Controller
          name={`${prefix}_razon_social`}
          control={control}
          render={({ field }) => (
            <input type="text" {...field} readOnly={!esManual} />
          )}
        />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>CIF</label>
          <Controller
            name={`${prefix}_cif`}
            control={control}
            render={({ field }) => (
              <input type="text" {...field} readOnly={!esManual} />
            )}
          />
        </div>
        <div className="form-group">
          <label>Teléfono</label>
          <Controller
            name={`${prefix}_tel`}
            control={control}
            render={({ field }) => (
              <input type="tel" {...field} readOnly={!esManual} />
            )}
          />
        </div>
      </div>
      <div className="form-group">
        <label>Dirección</label>
        <Controller
          name={`${prefix}_direccion`}
          control={control}
          render={({ field }) => (
            <input type="text" {...field} readOnly={!esManual} />
          )}
        />
      </div>
    </div>
  );
}

// Selector reutilizable de cargador contractual, común a carta de máquina y de árido.
// Prioridad al preseleccionar: cargador contractual de la obra de destino > empresa propia (operador) > "otro" (texto libre).
function CargadorContractualSelector({
  control,
  setValue,
  watch,
  clientes,
  operadorInfo,
  obraCargador,
}) {
  const selectorName = "cargador_contractual_selector";
  const seleccion = watch(selectorName);
  const esManual = seleccion === "otro";

  // Al cambiar la obra de destino (o su cargador contractual), re-derivar la preselección
  useEffect(() => {
    setValue(selectorName, obraCargador ? "obra" : "propio");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [obraCargador?.nombre, obraCargador?.cif, obraCargador?.direccion]);

  useEffect(() => {
    if (seleccion === undefined || seleccion === "") return;

    if (seleccion === "otro") return;

    if (seleccion === "obra") {
      setValue("cargador_contractual_nombre", obraCargador?.nombre || "");
      setValue("cargador_contractual_cif", obraCargador?.cif || "");
      setValue("cargador_contractual_direccion", obraCargador?.direccion || "");
      return;
    }

    if (seleccion === "propio") {
      setValue("cargador_contractual_nombre", operadorInfo?.nombre || "");
      setValue("cargador_contractual_cif", operadorInfo?.cif || "");
      setValue("cargador_contractual_direccion", operadorInfo?.direccion || "");
      return;
    }

    const cliente = clientes.find((c) => String(c.id) === String(seleccion));
    setValue("cargador_contractual_nombre", cliente?.razon_social || "");
    setValue("cargador_contractual_cif", cliente?.cif || "");
    setValue("cargador_contractual_direccion", cliente?.direccion || "");
  }, [seleccion, clientes, operadorInfo, obraCargador, setValue]);

  return (
    <div className="form-section">
      <h2>Cargador Contractual</h2>
      <div className="form-group">
        <label>Selecciona cargador contractual</label>
        <Controller
          name={selectorName}
          control={control}
          render={({ field }) => (
            <select {...field}>
              {obraCargador && (
                <option value="obra">
                  ★ {obraCargador.nombre} (de la obra de destino)
                </option>
              )}
              {operadorInfo && (
                <option value="propio">
                  {operadorInfo.nombre} (empresa propia)
                </option>
              )}
              {ordenarPorTexto(clientes, (c) => c.razon_social).map((cliente) => (
                <option key={cliente.id} value={cliente.id}>
                  {cliente.razon_social}
                </option>
              ))}
              <option value="otro">— Otro (rellenar manualmente) —</option>
            </select>
          )}
        />
      </div>

      <div className="form-group">
        <label>Razón Social</label>
        <Controller
          name="cargador_contractual_nombre"
          control={control}
          render={({ field }) => (
            <input type="text" {...field} readOnly={!esManual} />
          )}
        />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>CIF</label>
          <Controller
            name="cargador_contractual_cif"
            control={control}
            render={({ field }) => (
              <input type="text" {...field} readOnly={!esManual} />
            )}
          />
        </div>
        <div className="form-group">
          <label>Dirección</label>
          <Controller
            name="cargador_contractual_direccion"
            control={control}
            render={({ field }) => (
              <input type="text" {...field} readOnly={!esManual} />
            )}
          />
        </div>
      </div>
    </div>
  );
}

function CartaPorteForm() {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const isOnline = useConnectionStatus();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      fecha: today,
    },
  });

  const user = useSelector((state) => state?.auth?.currentUser?.data);
  const rol = useSelector((state) => state?.auth?.currentUser?.rol);
  const esConductor = rol === "conductor";
  const maquinas = useSelector((state) => state?.app?.maquinas || []);
  const plantasArido = useSelector((state) => state?.app?.plantasArido || []);
  const obras = useSelector((state) => state?.app?.obras || []);
  const vertederos = useSelector((state) => state?.app?.vertederos || []);

  const [tipo, setTipo] = useState(location?.state?.tipo || "maquina");
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dataLoadTimeout, setDataLoadTimeout] = useState(false);
  const [plantaSeleccionada, setPlantaSeleccionada] = useState(null);
  const [materialesHabilitados, setMaterialesHabilitados] = useState([]);
  const [clientesPorte, setClientesPorte] = useState([]);
  const [operadorInfo, setOperadorInfo] = useState(null);
  const [conductoresList, setConductoresList] = useState([]);
  const [camionesList, setCamionesList] = useState([]);
  // Listas de los desplegables, ordenadas alfabéticamente
  const obrasOrdenadas = useMemo(
    () => ordenarPorTexto(obras, (o) => o.nombre || o.obra_nombre),
    [obras],
  );
  const maquinasOrdenadas = useMemo(
    () => ordenarPorTexto(maquinas, (m) => m.nombre),
    [maquinas],
  );
  const plantasOrdenadas = useMemo(
    () => ordenarPorTexto(plantasArido, (p) => p.nombre),
    [plantasArido],
  );
  const camionesOrdenados = useMemo(
    () => ordenarPorTexto(camionesList, (c) => c.matricula),
    [camionesList],
  );
  const selectedPlantaId = watch("planta_arido_id");
  const origenObraIdMaquina = watch("origen_obra_id");

  // Usar ref para asegurar que el fetch se ejecute solo una vez
  const dataFetchedRef = useRef(false);

  // Timeout para mostrar mensaje si tarda mucho
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isLoadingData) {
        setDataLoadTimeout(true);
      }
    }, 3000);

    return () => clearTimeout(timeout);
  }, [isLoadingData]);

  // Cargar datos necesarios cuando el componente se monta
  useEffect(() => {
    const loadData = async () => {
      // Si ya se ejecutó este efecto, no hacer nada
      if (dataFetchedRef.current) {
        setIsLoadingData(false);
        return;
      }

      // Si no hay usuario, no hacer nada
      if (!user) {
        setIsLoadingData(false);
        return;
      }

      // Si no hay conexión, marcar como que no hay conexión pero permitir que continúe
      // (ya mostrará el mensaje de sin conexión)
      if (!isOnline) {
        setIsLoadingData(false);
        return;
      }

      setIsLoadingData(true);
      try {
        console.log("Cargando datos del formulario...");

        // Cargar obras
        console.log(
          "Cargando obras para operador:",
          user?.operador,
          "conductor_id:",
          user?.id,
        );
        const obrasResponse = await fetchObras({
          operador: user?.operador,
          conductor_id: user?.id,
        });
        console.log("Obras respuesta:", obrasResponse);
        if (Array.isArray(obrasResponse) && obrasResponse.length > 0) {
          dispatch(setObras(obrasResponse));
        } else {
          console.warn(
            "Las obras no son un array o están vacías",
            obrasResponse,
          );
        }

        // Cargar máquinas
        console.log("Cargando máquinas para operador:", user?.operador);
        try {
          const maquinasResponse = await fetchMaquinas(user?.operador);
          console.log("Máquinas respuesta:", maquinasResponse);
          if (Array.isArray(maquinasResponse) && maquinasResponse.length > 0) {
            dispatch(setMaquinas(maquinasResponse));
          } else {
            console.warn(
              "Las máquinas no son un array o están vacías",
              maquinasResponse,
            );
          }
        } catch (machineErr) {
          console.error("Error cargando máquinas:", machineErr?.message);
        }

        // Vertederos públicos + privados del operador (mismo criterio que el panel)
        try {
          const vertederosResponse = await fetchVertederos(user?.operador);
          if (Array.isArray(vertederosResponse)) {
            dispatch(setVertederos(vertederosResponse));
          }
        } catch (vertederosErr) {
          console.error("Error cargando vertederos:", vertederosErr?.message);
        }

        // Cargar clientes de porte (cargador/destinatario/cargador contractual), para ambos tipos
        try {
          const { clientes, operador } = await fetchClientesPorte(
            user?.operador,
          );
          setClientesPorte(clientes);
          setOperadorInfo(operador);
        } catch (clientesErr) {
          console.error(
            "Error cargando clientes de porte:",
            clientesErr?.message,
          );
        }

        // Cargar conductores (propios + subcontratados) y matrículas disponibles,
        // para poder elegir un conductor/matrícula distinto al del usuario logueado
        try {
          const conductoresResponse = await fetchConductoresCartaPorte(
            user?.operador,
          );
          setConductoresList(
            Array.isArray(conductoresResponse) ? conductoresResponse : [],
          );
        } catch (conductoresErr) {
          console.error("Error cargando conductores:", conductoresErr?.message);
        }
        try {
          const camionesResponse = await fetchCamionesCartaPorte(
            user?.operador,
          );
          setCamionesList(
            Array.isArray(camionesResponse) ? camionesResponse : [],
          );
        } catch (camionesErr) {
          console.error("Error cargando camiones:", camionesErr?.message);
        }

        // NO cargar plantas aquí - se cargarán cuando se seleccione una obra
        console.log(
          "NOTA: Las plantas de árido se cargarán cuando se seleccione una obra",
        );

        dataFetchedRef.current = true;
        console.log("Datos cargados exitosamente");
      } catch (err) {
        console.error("Error cargando datos:", err);
        const errorMsg =
          err?.response?.data?.message || err?.message || "Error desconocido";
        setError("Error al cargar los datos del formulario: " + errorMsg);
      } finally {
        setIsLoadingData(false);
      }
    };

    loadData();
  }, [isOnline, user, dispatch, tipo]);

  // Conductor por defecto: el usuario logueado, pero puede elegirse otro manualmente
  useEffect(() => {
    if (user?.id) {
      setValue("conductor_id", user.id);
    }
  }, [user, setValue]);

  // Auto-rellenar matrícula del camión (y góndola/remolque) asignados al conductor
  // (editable en máquina por si usa una combinación distinta; fija en árido)
  useEffect(() => {
    if (tipo === "maquina" && user?.matricula_camion_asignado) {
      setValue("matricula_camion", user.matricula_camion_asignado);
    }
    if (tipo === "maquina" && user?.matricula_remolque_asignado) {
      setValue("matricula_gondola", user.matricula_remolque_asignado);
    }
    if (tipo === "arido" && user?.matricula_camion_asignado) {
      setValue("matricula", user.matricula_camion_asignado);
    }
    if (tipo === "arido" && user?.matricula_remolque_asignado) {
      setValue("matricula_remolque", user.matricula_remolque_asignado);
    }
  }, [tipo, user, setValue]);

  // Al elegir un conductor distinto al propio, sugerir su matrícula asignada (editable después)
  const selectedConductorId = watch("conductor_id");
  useEffect(() => {
    if (
      !selectedConductorId ||
      String(selectedConductorId) === String(user?.id)
    )
      return;
    const conductor = conductoresList.find(
      (c) => String(c.id) === String(selectedConductorId),
    );
    if (!conductor?.matricula_camion_asignado) return;
    if (tipo === "maquina") {
      setValue("matricula_camion", conductor.matricula_camion_asignado);
      if (conductor.matricula_remolque_asignado) {
        setValue("matricula_gondola", conductor.matricula_remolque_asignado);
      }
    }
    if (tipo === "arido") {
      setValue("matricula", conductor.matricula_camion_asignado);
      if (conductor.matricula_remolque_asignado) {
        setValue("matricula_remolque", conductor.matricula_remolque_asignado);
      }
    }
  }, [selectedConductorId, conductoresList, user, tipo, setValue]);

  // Auto-rellenar peso al seleccionar una máquina conocida (editable)
  const selectedMaquinaId = watch("maquina_id");
  useEffect(() => {
    if (
      tipo === "maquina" &&
      selectedMaquinaId &&
      selectedMaquinaId !== "otro"
    ) {
      const maquina = maquinas.find(
        (m) => String(m.id) === String(selectedMaquinaId),
      );
      if (maquina?.peso) {
        setValue("maquina_peso", maquina.peso);
      }
    }
  }, [selectedMaquinaId, maquinas, tipo, setValue]);

  // Actualizar materiales cuando cambia planta
  useEffect(() => {
    if (selectedPlantaId) {
      const planta = plantasArido.find(
        (p) => p.id === parseInt(selectedPlantaId),
      );
      setPlantaSeleccionada(planta);

      // Extraer materiales_habilitados del pivot
      if (planta && planta.pivot && planta.pivot.materiales_habilitados) {
        try {
          const habilitados =
            typeof planta.pivot.materiales_habilitados === "string"
              ? JSON.parse(planta.pivot.materiales_habilitados)
              : planta.pivot.materiales_habilitados;
          console.log(
            "Materiales habilitados para planta",
            planta.id,
            ":",
            habilitados,
          );
          setMaterialesHabilitados(habilitados);
        } catch (e) {
          console.error("Error parsing materiales_habilitados:", e);
          setMaterialesHabilitados([]);
        }
      } else {
        setMaterialesHabilitados([]);
      }

      // Auto-rellenar datos del expedidor para tipo árido
      if (tipo === "arido" && planta) {
        console.log("Auto-rellenando expedidor con datos de planta:", planta);
        setValue("expedidor_razon_social", planta.expedidor_razon_social || "");
        setValue("expedidor_cif", planta.expedidor_cif || "");
        setValue("expedidor_tfno", planta.expedidor_tfno || "");
        setValue("expedidor_direccion", planta.direccion || "");
      }
    } else {
      setPlantaSeleccionada(null);
      setMaterialesHabilitados([]);
    }
  }, [selectedPlantaId, plantasArido, tipo, setValue]);

  // Destino: obra | vertedero_{id} | otro
  const selectedDestinoValue = watch("destino_obra_id");
  const destinoParsed = useMemo(
    () => parseDestinoValue(selectedDestinoValue),
    [selectedDestinoValue],
  );
  const selectedObraId = destinoParsed.obraId;

  const obrasNormales = useMemo(
    () =>
      (Array.isArray(obras) ? obras : []).filter(
        (o) => (o.tipo || "obra") !== "contenedor",
      ),
    [obras],
  );
  const direccionesContenedor = useMemo(
    () =>
      (Array.isArray(obras) ? obras : []).filter(
        (o) => o.tipo === "contenedor",
      ),
    [obras],
  );

  // Catálogo del conductor + vertederos_disponibles de cada obra asignada
  // (externos: privados del operador de la obra, no solo los de su empresa).
  const vertederosDestino = useMemo(() => {
    const byId = new Map();
    (Array.isArray(vertederos) ? vertederos : []).forEach((v) => {
      if (v?.id != null && !byId.has(v.id)) {
        byId.set(v.id, v);
      }
    });
    (Array.isArray(obras) ? obras : []).forEach((obra) => {
      const list =
        obra.vertederos_disponibles || obra.vertederosDisponibles || [];
      (Array.isArray(list) ? list : []).forEach((v) => {
        if (v?.id != null && !byId.has(v.id)) {
          byId.set(v.id, v);
        }
      });
      const prefId = obra.vertederos_id || obra.vertedero;
      const prefNombre = obra.vertederos_nombre;
      if (prefId && prefNombre && !byId.has(prefId)) {
        byId.set(prefId, { id: prefId, nombre: prefNombre });
      }
    });
    return Array.from(byId.values()).sort((a, b) =>
      String(a.nombre || "").localeCompare(String(b.nombre || ""), "es"),
    );
  }, [vertederos, obras]);

  // Cargador contractual predefinido en la obra de destino (común a máquina y árido)
  const obraCargadorContractual = useMemo(() => {
    if (!selectedObraId) return null;
    const obra = obras.find(
      (o) => (o.id || o.obra_id) === parseInt(selectedObraId, 10),
    );
    const nombre = obra?.cargador_contractual_nombre;
    if (!nombre) return null;
    return {
      nombre,
      cif: obra?.cargador_contractual_cif || "",
      direccion: obra?.cargador_contractual_direccion || "",
    };
  }, [selectedObraId, obras]);

  // Destinatario predefinido en la obra de destino
  useEffect(() => {
    if (!selectedObraId) return;
    const obra = obras.find(
      (o) => (o.id || o.obra_id) === parseInt(selectedObraId, 10),
    );
    if (!obra?.destinatario_nombre) return;

    if (obra.destinatario_cliente_id) {
      setValue("destinatario_selector", String(obra.destinatario_cliente_id));
      return;
    }

    setValue("destinatario_selector", "otro");
    setValue("destinatario_cliente_id", null);
    setValue("destinatario_razon_social", obra.destinatario_nombre || "");
    setValue("destinatario_cif", obra.destinatario_cif || "");
    setValue("destinatario_direccion", obra.destinatario_direccion || "");
  }, [selectedObraId, obras, setValue]);

  useEffect(() => {
    if (tipo !== "arido") return;

    const cargarTodasPlantas = () => {
      fetchPlantasArido(user?.operador)
        .then((plantas) => dispatch(setPlantasArido(plantas)))
        .catch((plantasErr) => {
          console.error(
            "Error cargando todas las plantas para destino no-obra:",
            plantasErr?.message,
          );
          dispatch(setPlantasArido([]));
        });
    };

    if (selectedObraId) {
      const obraSeleccionada = obras.find(
        (o) => (o.id || o.obra_id) === parseInt(selectedObraId, 10),
      );

      // Dirección de contenedor: sin plantas asociadas → listado completo
      if (obraSeleccionada?.tipo === "contenedor") {
        cargarTodasPlantas();
        return;
      }

      if (obraSeleccionada) {
        const plantasAsociadas =
          obraSeleccionada.plantas_arido ||
          obraSeleccionada.plantasArido ||
          obraSeleccionada.plantas ||
          [];
        dispatch(setPlantasArido(plantasAsociadas));
      } else {
        dispatch(setPlantasArido([]));
      }
    } else if (
      destinoParsed.esOtro ||
      destinoParsed.tipo === "vertedero" ||
      !selectedDestinoValue
    ) {
      // Sin obra concreta: todas las plantas (otro / vertedero / vacío)
      if (destinoParsed.esOtro || destinoParsed.tipo === "vertedero") {
        cargarTodasPlantas();
      }
    }
  }, [
    selectedObraId,
    selectedDestinoValue,
    destinoParsed.esOtro,
    destinoParsed.tipo,
    tipo,
    obras,
    dispatch,
    user?.operador,
  ]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    setError(null);

    try {
      // Preparar datos según tipo
      const cartaData = {
        tipo: tipo,
        fecha: formatFecha(data.fecha),
        comentarios: data.comentarios || null,
        // NO incluir 'ref' aquí - será generado por el backend usando Contadores
      };

      if (tipo === "maquina") {
        // Validaciones máquina
        const esOrigenOtro = data.origen_obra_id === "otro";
        const destino = parseDestinoValue(data.destino_obra_id);
        const esMaquinaOtro = data.maquina_id === "otro";

        if (!data.origen_obra_id) {
          throw new Error("El origen es requerido");
        }
        if (esOrigenOtro && !data.origen_otro) {
          throw new Error("Describe el origen");
        }
        if (!data.destino_obra_id) {
          throw new Error("El destino es requerido");
        }
        if (destino.esOtro && !data.destino_otro) {
          throw new Error("Describe el destino");
        }
        if (!data.maquina_id) {
          throw new Error("Debe seleccionar una carga");
        }
        if (esMaquinaOtro && !data.maquina_otro) {
          throw new Error("Describe la carga");
        }
        if (!data.maquina_peso) {
          throw new Error("El peso de la carga es requerido");
        }
        if (!data.matricula_camion) {
          throw new Error("La matrícula del camión es requerida");
        }

        Object.assign(cartaData, {
          conductor_id: data.conductor_id || user?.id,
          origen_obra_id: esOrigenOtro ? null : data.origen_obra_id,
          origen_otro: esOrigenOtro ? data.origen_otro : null,
          destino_obra_id: destino.obraId,
          destino_vertedero_id: destino.vertederoId,
          destino_otro: destino.esOtro ? data.destino_otro : null,
          cargador_contractual_nombre: data.cargador_contractual_nombre || null,
          cargador_contractual_cif: data.cargador_contractual_cif || null,
          cargador_contractual_direccion:
            data.cargador_contractual_direccion || null,
          destinatario_cliente_id: data.destinatario_cliente_id || null,
          destinatario_razon_social: data.destinatario_razon_social || null,
          destinatario_cif: data.destinatario_cif || null,
          destinatario_direccion: data.destinatario_direccion || null,
          destinatario_tel: data.destinatario_tel || null,
          maquina_id: esMaquinaOtro ? null : data.maquina_id,
          maquina_otro: esMaquinaOtro ? data.maquina_otro : null,
          maquina_peso: data.maquina_peso,
          matricula_camion: data.matricula_camion,
          matricula_gondola: data.matricula_gondola || null,
        });
      } else if (tipo === "arido") {
        // Validaciones árido
        const esPlantaOtro = data.planta_arido_id === "otro";
        const destino = parseDestinoValue(data.destino_obra_id);
        const esMaterialOtro = data.material_arido_id === "otro";

        if (!data.planta_arido_id) {
          throw new Error("El origen (planta de árido) es requerido");
        }
        if (esPlantaOtro && !data.origen_planta_otro) {
          throw new Error("Describe el origen");
        }
        if (!data.destino_obra_id) {
          throw new Error("El destino es requerido");
        }
        if (destino.esOtro && !data.destino_otro) {
          throw new Error("Describe el destino");
        }
        if (!data.material_arido_id) {
          throw new Error("Debe seleccionar un material");
        }
        if (esMaterialOtro && !data.tipo_material_otro) {
          throw new Error("Describe el material");
        }
        if (!data.matricula) {
          throw new Error("La matrícula es requerida");
        }

        Object.assign(cartaData, {
          planta_arido_id: esPlantaOtro ? null : data.planta_arido_id,
          origen_planta_otro: esPlantaOtro ? data.origen_planta_otro : null,
          expedidor_razon_social: data.expedidor_razon_social || null,
          expedidor_cif: data.expedidor_cif || null,
          expedidor_direccion: data.expedidor_direccion || null,
          expedidor_tfno: data.expedidor_tfno || null,
          destino_obra_id: destino.obraId,
          destino_vertedero_id: destino.vertederoId,
          destino_otro: destino.esOtro ? data.destino_otro : null,
          cargador_contractual_nombre: data.cargador_contractual_nombre || null,
          cargador_contractual_cif: data.cargador_contractual_cif || null,
          cargador_contractual_direccion:
            data.cargador_contractual_direccion || null,
          destinatario_cliente_id: data.destinatario_cliente_id || null,
          destinatario_razon_social: data.destinatario_razon_social || null,
          destinatario_cif: data.destinatario_cif || null,
          destinatario_direccion: data.destinatario_direccion || null,
          destinatario_tel: data.destinatario_tel || null,
          transportista_efectivo: data.transportista_efectivo || null,
          conductor_id: data.conductor_id || user?.id,
          material_arido_id: esMaterialOtro ? null : data.material_arido_id,
          tipo_material_otro: esMaterialOtro ? data.tipo_material_otro : null,
          m3: data.m3 || null,
          peso: data.peso || null,
          matricula: data.matricula,
          matricula_remolque: data.matricula_remolque || null,
        });
      }

      // Verificar conexión
      if (!isOnline) {
        // Guardar como pendiente si no hay conexión
        dispatch(setCartaPortePendiente(cartaData));
        history.push("/ListScreen");
        alert(
          "No hay conexión. La carta de porte se creará cuando haya conexión disponible.",
        );
      } else {
        // Crear carta de porte
        const response = await crearCartaPorte(cartaData);

        if (response?.id) {
          history.push("/ListScreen");
          // Mostrar confirmación
          // alert("Carta de porte creada correctamente. Se está generando el PDF...");
        }
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Error al crear la carta de porte",
      );
      console.error("Error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return "";
    // Convertir a formato d/m/Y
    if (fecha instanceof Date) {
      const day = String(fecha.getDate()).padStart(2, "0");
      const month = String(fecha.getMonth() + 1).padStart(2, "0");
      const year = fecha.getFullYear();
      return `${day}/${month}/${year}`; // d/m/Y format
    }
    return fecha;
  };

  return (
    <div className="carta-porte-form-container">
      <LogoHeader />

      <div className="form-wrapper">
        {/* Header con flecha atrás - estilo DI */}
        <div className="back-header">
          <button
            className="back-link"
            onClick={() => history.push("/ListScreen")}
          >
            <FiChevronLeft /> Datos Carta
          </button>
        </div>

        {!user && (
          <div className="error-alert">
            <strong>Error:</strong> No hay usuario autenticado. Por favor,
            inicia sesión nuevamente.
          </div>
        )}

        {!isOnline && (
          <div className="error-alert">
            <strong>Atención:</strong> Sin conexión a internet. Se guardará como
            pendiente.
          </div>
        )}

        {error && (
          <div className="error-alert">
            <strong>Error:</strong> {error}
          </div>
        )}

        {isLoadingData && (
          <div className="loading-alert">
            <div className="spinner"></div>
            <div>
              <div>Cargando datos del formulario...</div>
              {dataLoadTimeout && (
                <div
                  style={{ fontSize: "12px", marginTop: "8px", opacity: 0.8 }}
                >
                  Esto está tardando más de lo esperado. Verifica tu conexión.
                </div>
              )}
            </div>
          </div>
        )}

        {!isLoadingData && user && (
          <>
            {obras.length === 0 && tipo === "maquina" && (
              <div className="error-alert">
                <strong>Aviso:</strong> No hay obras disponibles. Verifica que
                tengas obras asignadas.
              </div>
            )}
            {maquinas.length === 0 && tipo === "maquina" && (
              <div className="error-alert">
                <strong>Aviso:</strong> No hay cargas disponibles. Verifica que
                tengas cargas registradas.
              </div>
            )}
            {plantasArido.length === 0 && tipo === "arido" && (
              <div className="error-alert">
                <strong>Aviso:</strong> No hay plantas de árido disponibles.
                Verifica que tengas plantas registradas.
              </div>
            )}
            <form onSubmit={handleSubmit(onSubmit)} className="carta-form">
              {/* DATOS COMUNES */}
              <div className="form-section">
                <h2>Datos de la Carta</h2>

                <div className="form-group">
                  <label>Fecha *</label>
                  <Controller
                    name="fecha"
                    control={control}
                    rules={{ required: "La fecha es requerida" }}
                    render={({ field }) => {
                      const dateValue =
                        field.value instanceof Date
                          ? `${field.value.getFullYear()}-${String(field.value.getMonth() + 1).padStart(2, "0")}-${String(field.value.getDate()).padStart(2, "0")}`
                          : "";
                      return (
                        <input
                          type="date"
                          value={dateValue}
                          onChange={(e) => {
                            const date = new Date(e.target.value + "T00:00:00");
                            field.onChange(date);
                          }}
                        />
                      );
                    }}
                  />
                  {errors.fecha && (
                    <span className="error">{errors.fecha.message}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Conductor *</label>
                  <Controller
                    name="conductor_id"
                    control={control}
                    rules={{ required: "Selecciona un conductor" }}
                    render={({ field }) => (
                      <select {...field} disabled={esConductor}>
                        {esConductor
                          ? user?.id && (
                              <option value={user.id}>
                                {user?.nombre || "Yo"}
                              </option>
                            )
                          : Array.isArray(conductoresList) &&
                              conductoresList.length > 0
                            ? ordenarPorTexto(conductoresList, (c) => c.nombre).map((conductor) => (
                                <option key={conductor.id} value={conductor.id}>
                                  {conductor.nombre}
                                </option>
                              ))
                            : user?.id && (
                                <option value={user.id}>
                                  {user?.nombre || "Yo"}
                                </option>
                              )}
                      </select>
                    )}
                  />
                  {errors.conductor_id && (
                    <span className="error">{errors.conductor_id.message}</span>
                  )}
                </div>
              </div>

              {/* FORMULARIO MÁQUINA */}
              {tipo === "maquina" && (
                <>
                  <div className="form-section">
                    <h2>Origen</h2>
                    <div className="form-group">
                      <label>Obra de Origen *</label>
                      <Controller
                        name="origen_obra_id"
                        control={control}
                        rules={{ required: "Selecciona una obra" }}
                        render={({ field }) => (
                          <select {...field}>
                            <option value="">Selecciona una obra...</option>
                            {Array.isArray(obras) && obras.length > 0 ? (
                              obrasOrdenadas.map((obra) => (
                                <option
                                  key={obra.id || obra.obra_id}
                                  value={obra.id || obra.obra_id}
                                >
                                  {obra.nombre || obra.obra_nombre}
                                </option>
                              ))
                            ) : (
                              <option disabled>No hay obras disponibles</option>
                            )}
                            <option value="otro">— Otro (texto libre) —</option>
                          </select>
                        )}
                      />
                      {errors.origen_obra_id && (
                        <span className="error">
                          {errors.origen_obra_id.message}
                        </span>
                      )}
                    </div>
                    {origenObraIdMaquina === "otro" && (
                      <div className="form-group">
                        <label>Descripción del Origen *</label>
                        <Controller
                          name="origen_otro"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              {...field}
                              placeholder="Indica el origen..."
                            />
                          )}
                        />
                      </div>
                    )}
                  </div>

                  <div className="form-section">
                    <h2>Destino</h2>
                    <div className="form-group">
                      <label>Destino *</label>
                      <Controller
                        name="destino_obra_id"
                        control={control}
                        rules={{ required: "Selecciona un destino" }}
                        render={({ field }) => (
                          <select {...field}>
                            <option value="">Selecciona destino...</option>
                            {Array.isArray(obras) && obras.length > 0 && (
                              <optgroup label="Obras">
                                {obrasOrdenadas.map((obra) => (
                                  <option
                                    key={obra.id || obra.obra_id}
                                    value={obra.id || obra.obra_id}
                                  >
                                    {obra.nombre || obra.obra_nombre}
                                  </option>
                                ))}
                              </optgroup>
                            )}
                            {vertederosDestino.length > 0 && (
                              <optgroup label="Vertederos">
                                {vertederosDestino.map((v) => (
                                  <option
                                    key={`vertedero_${v.id}`}
                                    value={`vertedero_${v.id}`}
                                  >
                                    {v.nombre}
                                  </option>
                                ))}
                              </optgroup>
                            )}
                            <option value="otro">— Otro (texto libre) —</option>
                          </select>
                        )}
                      />
                      {errors.destino_obra_id && (
                        <span className="error">
                          {errors.destino_obra_id.message}
                        </span>
                      )}
                    </div>
                    {destinoParsed.esOtro && (
                      <div className="form-group">
                        <label>Descripción del Destino *</label>
                        <Controller
                          name="destino_otro"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              {...field}
                              placeholder="Indica el destino..."
                            />
                          )}
                        />
                      </div>
                    )}
                  </div>

                  <CargadorContractualSelector
                    control={control}
                    setValue={setValue}
                    watch={watch}
                    clientes={clientesPorte}
                    operadorInfo={operadorInfo}
                    obraCargador={obraCargadorContractual}
                  />

                  <EmpresaSelector
                    control={control}
                    setValue={setValue}
                    watch={watch}
                    prefix="destinatario"
                    label="Destinatario"
                    clientes={clientesPorte}
                    operadorInfo={operadorInfo}
                  />

                  <div className="form-section">
                    <h2>Carga a Transportar *</h2>
                    <div className="form-group">
                      <label>Carga *</label>
                      <Controller
                        name="maquina_id"
                        control={control}
                        rules={{ required: "Selecciona una carga" }}
                        render={({ field }) => (
                          <select {...field}>
                            <option value="">Selecciona una carga...</option>
                            {maquinasOrdenadas.map((maquina) => (
                              <option key={maquina.id} value={maquina.id}>
                                {maquina.nombre}
                              </option>
                            ))}
                            <option value="otro">— Otro (texto libre) —</option>
                          </select>
                        )}
                      />
                      {errors.maquina_id && (
                        <span className="error">
                          {errors.maquina_id.message}
                        </span>
                      )}
                    </div>

                    {selectedMaquinaId === "otro" && (
                      <div className="form-group">
                        <label>Descripción de la Carga *</label>
                        <Controller
                          name="maquina_otro"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              {...field}
                              placeholder="Describe la carga..."
                            />
                          )}
                        />
                      </div>
                    )}

                    <div className="form-group">
                      <label>Peso (Tn) *</label>
                      <Controller
                        name="maquina_peso"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="number"
                            step="0.01"
                            {...field}
                            placeholder="Ej: 22.5"
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div className="form-section">
                    <h2>Conductor y Vehículo</h2>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Matrícula Camión *</label>
                        <Controller
                          name="matricula_camion"
                          control={control}
                          render={({ field }) => (
                            <select
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                const camion = camionesList.find(
                                  (c) => c.matricula === e.target.value,
                                );
                                if (camion?.remolque) {
                                  setValue(
                                    "matricula_gondola",
                                    camion.remolque,
                                  );
                                }
                              }}
                            >
                              <option value="">Selecciona matrícula...</option>
                              {camionesOrdenados.map((camion) => (
                                <option
                                  key={camion.matricula}
                                  value={camion.matricula}
                                >
                                  {camion.matricula}
                                </option>
                              ))}
                            </select>
                          )}
                        />
                      </div>
                      <div className="form-group">
                        <label>Matrícula Remolque</label>
                        <Controller
                          name="matricula_gondola"
                          control={control}
                          render={({ field }) => (
                            <input type="text" {...field} />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* FORMULARIO ÁRIDO */}
              {tipo === "arido" && (
                <>
                  <div className="form-section">
                    <h2>Destino *</h2>
                    <div className="form-group">
                      <label>Destino *</label>
                      <Controller
                        name="destino_obra_id"
                        control={control}
                        rules={{ required: "Selecciona un destino" }}
                        render={({ field }) => (
                          <select {...field}>
                            <option value="">Selecciona destino...</option>
                            {obrasNormales.length > 0 && (
                              <optgroup label="Obras">
                                {ordenarPorTexto(obrasNormales, (o) => o.nombre || o.obra_nombre).map((obra) => (
                                  <option
                                    key={obra.id || obra.obra_id}
                                    value={obra.id || obra.obra_id}
                                  >
                                    {obra.nombre || obra.obra_nombre}
                                  </option>
                                ))}
                              </optgroup>
                            )}
                            {direccionesContenedor.length > 0 && (
                              <optgroup label="Direcciones contenedor">
                                {direccionesContenedor.map((obra) => (
                                  <option
                                    key={`cont_${obra.id || obra.obra_id}`}
                                    value={obra.id || obra.obra_id}
                                  >
                                    {obra.nombre ||
                                      obra.obra_nombre ||
                                      [
                                        obra.cliente_nombre,
                                        obra.direccion || obra.obra_direccion,
                                      ]
                                        .filter(Boolean)
                                        .join(" — ")}
                                  </option>
                                ))}
                              </optgroup>
                            )}
                            {vertederosDestino.length > 0 && (
                              <optgroup label="Vertederos">
                                {vertederosDestino.map((v) => (
                                  <option
                                    key={`vertedero_${v.id}`}
                                    value={`vertedero_${v.id}`}
                                  >
                                    {v.nombre}
                                  </option>
                                ))}
                              </optgroup>
                            )}
                            <option value="otro">— Otro (texto libre) —</option>
                          </select>
                        )}
                      />
                      {errors.destino_obra_id && (
                        <span className="error">
                          {errors.destino_obra_id.message}
                        </span>
                      )}
                    </div>
                    {destinoParsed.esOtro && (
                      <div className="form-group">
                        <label>Descripción del Destino *</label>
                        <Controller
                          name="destino_otro"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              {...field}
                              placeholder="Indica el destino..."
                            />
                          )}
                        />
                      </div>
                    )}
                  </div>

                  <CargadorContractualSelector
                    control={control}
                    setValue={setValue}
                    watch={watch}
                    clientes={clientesPorte}
                    operadorInfo={operadorInfo}
                    obraCargador={obraCargadorContractual}
                  />

                  <div className="form-section">
                    <h2>Origen - Planta de Árido *</h2>
                    <div className="form-group">
                      <label>Planta de Árido *</label>
                      <Controller
                        name="planta_arido_id"
                        control={control}
                        rules={{ required: "Selecciona una planta" }}
                        render={({ field }) => (
                          <select {...field}>
                            <option value="">Selecciona una planta...</option>
                            {Array.isArray(plantasArido) &&
                            plantasArido.length > 0 ? (
                              plantasOrdenadas.map((planta) => (
                                <option key={planta.id} value={planta.id}>
                                  {planta.nombre}
                                </option>
                              ))
                            ) : (
                              <option disabled>
                                No hay plantas disponibles
                              </option>
                            )}
                            <option value="otro">— Otro (texto libre) —</option>
                          </select>
                        )}
                      />
                      {errors.planta_arido_id && (
                        <span className="error">
                          {errors.planta_arido_id.message}
                        </span>
                      )}
                    </div>
                    {selectedPlantaId === "otro" && (
                      <div className="form-group">
                        <label>Descripción del Origen *</label>
                        <Controller
                          name="origen_planta_otro"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              {...field}
                              placeholder="Describe el origen..."
                            />
                          )}
                        />
                      </div>
                    )}
                  </div>

                  <div className="form-section">
                    <h2>Expedidor / Cargador Efectivo</h2>
                    <div className="form-group">
                      <label>Razón Social *</label>
                      <Controller
                        name="expedidor_razon_social"
                        control={control}
                        rules={{
                          required:
                            "La razón social del expedidor es requerida",
                        }}
                        render={({ field }) => <input type="text" {...field} />}
                      />
                      {errors.expedidor_razon_social && (
                        <span className="error">
                          {errors.expedidor_razon_social.message}
                        </span>
                      )}
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>CIF *</label>
                        <Controller
                          name="expedidor_cif"
                          control={control}
                          rules={{
                            required: "El CIF del expedidor es requerido",
                          }}
                          render={({ field }) => (
                            <input type="text" {...field} />
                          )}
                        />
                        {errors.expedidor_cif && (
                          <span className="error">
                            {errors.expedidor_cif.message}
                          </span>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Teléfono</label>
                        <Controller
                          name="expedidor_tfno"
                          control={control}
                          render={({ field }) => (
                            <input type="tel" {...field} />
                          )}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Dirección</label>
                      <Controller
                        name="expedidor_direccion"
                        control={control}
                        render={({ field }) => <input type="text" {...field} />}
                      />
                    </div>
                  </div>

                  <EmpresaSelector
                    control={control}
                    setValue={setValue}
                    watch={watch}
                    prefix="destinatario"
                    label="Destinatario"
                    clientes={clientesPorte}
                    operadorInfo={operadorInfo}
                  />

                  {/* <div className="form-section">
                    <h2>Transportista Efectivo</h2>
                    <div className="form-group">
                      <label>Transportista (Opcional)</label>
                      <Controller
                        name="transportista_efectivo"
                        control={control}
                        render={({ field }) => (
                          <input type="text" {...field} placeholder="Nombre transportista (opcional)" />
                        )}
                      />
                    </div>
                  </div> */}

                  <div className="form-section">
                    <h2>Material Árido a Transportar *</h2>
                    <div className="form-group">
                      <label>Material *</label>
                      <Controller
                        name="material_arido_id"
                        control={control}
                        rules={{ required: "Selecciona un material" }}
                        render={({ field }) => {
                          const materialesDisponibles = (
                            plantaSeleccionada?.materiales || []
                          ).filter((material) =>
                            materialesHabilitados.includes(
                              material.id.toString(),
                            ),
                          );
                          return (
                            <select {...field}>
                              <option value="">
                                Selecciona un material...
                              </option>
                              {ordenarPorTexto(materialesDisponibles, (m) => m.nombre).map((material) => (
                                <option key={material.id} value={material.id}>
                                  {material.nombre}
                                </option>
                              ))}
                              <option value="otro">
                                — Otro (texto libre) —
                              </option>
                            </select>
                          );
                        }}
                      />
                      {errors.material_arido_id && (
                        <span className="error">
                          {errors.material_arido_id.message}
                        </span>
                      )}
                    </div>

                    {watch("material_arido_id") === "otro" && (
                      <div className="form-group">
                        <label>Descripción del Material *</label>
                        <Controller
                          name="tipo_material_otro"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              {...field}
                              placeholder="Describe el material..."
                            />
                          )}
                        />
                      </div>
                    )}
                  </div>

                  <div className="form-section">
                    <h2>Cantidades</h2>
                    <div className="form-row">
                      <div className="form-group">
                        <label>M³</label>
                        <Controller
                          name="m3"
                          control={control}
                          render={({ field }) => (
                            <input type="number" step="0.01" {...field} />
                          )}
                        />
                      </div>
                      <div className="form-group">
                        <label>Peso (Tn)</label>
                        <Controller
                          name="peso"
                          control={control}
                          render={({ field }) => (
                            <input type="number" step="0.01" {...field} />
                          )}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-section">
                    <h2>Conductor y Vehículo</h2>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Matrícula Camión *</label>
                        <Controller
                          name="matricula"
                          control={control}
                          render={({ field }) => (
                            <select
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                const camion = camionesList.find(
                                  (c) => c.matricula === e.target.value,
                                );
                                if (camion?.remolque) {
                                  setValue(
                                    "matricula_remolque",
                                    camion.remolque,
                                  );
                                }
                              }}
                            >
                              <option value="">Selecciona matrícula...</option>
                              {camionesOrdenados.map((camion) => (
                                <option
                                  key={camion.matricula}
                                  value={camion.matricula}
                                >
                                  {camion.matricula}
                                </option>
                              ))}
                            </select>
                          )}
                        />
                      </div>
                      <div className="form-group">
                        <label>Matrícula Remolque</label>
                        <Controller
                          name="matricula_remolque"
                          control={control}
                          render={({ field }) => (
                            <>
                              <input
                                type="text"
                                {...field}
                                list="remolques-list-arido"
                              />
                              <datalist id="remolques-list-arido">
                                {Array.from(
                                  new Set(
                                    camionesList
                                      .map((c) => c.remolque)
                                      .filter(Boolean),
                                  ),
                                ).map((remolque) => (
                                  <option key={remolque} value={remolque} />
                                ))}
                              </datalist>
                            </>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-section">
                    <div className="form-group">
                      <h2>Comentarios / Observaciones</h2>
                      <label>Comentarios</label>
                      <Controller
                        name="comentarios"
                        control={control}
                        render={({ field }) => (
                          <textarea
                            {...field}
                            rows="3"
                            placeholder="Observaciones o notas adicionales..."
                          />
                        )}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* BOTONES */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => history.push("/ListScreen")}
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={isLoading}
                >
                  {isLoading ? "Creando..." : "Crear Carta de Porte"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default CartaPorteForm;
